import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { GET_HISTORY_BY_PATIENT } from "./queries";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Button, UncontrolledTooltip } from "reactstrap";
import { isEmpty } from "lodash";
import moment from "moment";

const INITIAL_STATE = {
  dataHistory: [],
  isOpenModalDetail: false,
  dataSelected: {},
};

const getDiagnosisTooltipId = (rowId, index) =>
  `appointment-diagnosis-history-${rowId || "row"}-${index}`;

export default function useActions({
  patientId,
  appointmentHistoryDiagnoses,
  onChange,
  loadDiagnoses,
  isPageModalHistory,
  isActive = true,
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const { params } = useParams();
  // Todas las pestañas se montan a la vez; el historial (sin paginar) se pide
  // la primera vez que se abre la pestaña y luego se conserva.
  const [wasOpened, setWasOpened] = useState(isActive);
  useEffect(() => {
    if (isActive) setWasOpened(true);
  }, [isActive]);

  const { loading: loadingHistoryByPatient } = useQuery(
    GET_HISTORY_BY_PATIENT,
    {
      skip: !patientId || !wasOpened,
      variables: {
        where: {
          patientId,
          getNotAppointmentId: Number(params) || undefined,
        },
        relations: [
          "appointmentHistoryDiagnoses",
          "appointmentHistoryDiagnoses.diagnoses",
          "medicalStaff",
          "appointment",
          "appointment.speciality",
        ],
      },
      onCompleted: (data) => {
        setState((prevState) => ({
          ...prevState,
          dataHistory: data?.getHistoryByPatientId || [],
        }));
      },
    },
  );

  const diagnoses = useMemo(
    () => appointmentHistoryDiagnoses || [],
    [appointmentHistoryDiagnoses],
  );

  const hasPrimaryDiagnosis = useMemo(
    () => diagnoses.some((item) => item.order === 1),
    [diagnoses],
  );

  const onChangeDiagnoses = useCallback((key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }, []);

  const toggleModalDetail = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isOpenModalDetail: !prevState.isOpenModalDetail,
      dataSelected: prevState.isOpenModalDetail ? {} : prevState.dataSelected,
    }));
  }, []);

  const handleOpenDetail = useCallback((dataSelected) => {
    setState((prevState) => ({
      ...prevState,
      isOpenModalDetail: true,
      dataSelected,
    }));
  }, []);

  const removeDiagnosis = useCallback(
    (order) => {
      const updatedDiagnoses = diagnoses.filter(
        (diagnosis) => diagnosis.order !== order,
      );
      onChange("tabDiagnoses", updatedDiagnoses, "appointmentHistoryDiagnoses");
    },
    [diagnoses, onChange],
  );

  const handleDiagnosisChange = useCallback(
    (item, order) => {
      if (!item) {
        removeDiagnosis(order);
        return;
      }

      const updatedDiagnoses = diagnoses.filter(
        (diagnosis) => diagnosis.order !== order,
      );

      onChange(
        "tabDiagnoses",
        [...updatedDiagnoses, { patientId, diagnoses: item, order }],
        "appointmentHistoryDiagnoses",
      );
    },
    [diagnoses, onChange, patientId, removeDiagnosis],
  );

  const getDiagnosisSelectProps = useCallback(
    (order) => ({
      placeholder: `Seleccionar Diagnostico ${order}`,
      loadingMessage: () => "Cargando...",
      noOptionsMessage: () => "No se encontraron opciones",
      loadOptions: loadDiagnoses,
      isClearable: order !== 1,
      getOptionValue: (item) => String(item?.id ?? item?.description ?? order),
      getOptionLabel: (item) => item.description,
      isDisabled:
        (order !== 1 && !hasPrimaryDiagnosis) || Boolean(isPageModalHistory),
      value: isEmpty(diagnoses)
        ? null
        : diagnoses.find((item) => item.order === order)?.diagnoses,
      onChange: (item) => handleDiagnosisChange(item, order),
    }),
    [
      diagnoses,
      handleDiagnosisChange,
      hasPrimaryDiagnosis,
      isPageModalHistory,
      loadDiagnoses,
    ],
  );

  const columnsHistory = useMemo(
    () => [
      {
        Header: "Fecha",
        className: "text-center",
        width: 120,
        Cell: ({ original }) => moment(original?.date).format("DD-MM-YYYY"),
      },
      {
        Header: "Medico Tratante",
        minWidth: 300,
        Cell: ({ original }) =>
          `${original?.medicalStaff?.firstName ?? ""} ${
            original?.medicalStaff?.lastName ?? ""
          }`.trim() || "N/A",
      },
      {
        Header: "Especialidad",
        minWidth: 300,
        Cell: ({ original }) =>
          original?.appointment?.speciality?.name || "N/A",
      },
      {
        Header: "Diagnostico",
        className: "p-0",
        width: 450,
        Cell: ({ original }) => {
          const historyDiagnoses = original?.appointmentHistoryDiagnoses || [];

          return (
            <div>
              {isEmpty(historyDiagnoses)
                ? "Sin diagnostico"
                : historyDiagnoses.map((item, indexMap) => {
                    const tooltipId = getDiagnosisTooltipId(
                      original?.id,
                      indexMap,
                    );

                    return (
                      <div
                        key={item.id || tooltipId}
                        className="d-inline-block bg-gray-300 mb-1 px-1 m-1 text-truncate"
                        style={{
                          width: "125px",
                          borderRadius: "8px",
                        }}
                        id={tooltipId}
                      >
                        {item.diagnoses?.description}
                        <UncontrolledTooltip
                          placement="bottom"
                          target={tooltipId}
                        >
                          {item.diagnoses?.description}
                        </UncontrolledTooltip>
                      </div>
                    );
                  })}
            </div>
          );
        },
      },
      {
        Header: "Opciones",
        width: 150,
        Cell: ({ original }) => (
          <Button
            color="secondary"
            type="button"
            className="btn-color-gray card-header-action"
            onClick={() => handleOpenDetail(original)}
          >
            <i className="fa fa-eye" />
            {" Ver detalles"}
          </Button>
        ),
      },
    ],
    [handleOpenDetail],
  );

  return [
    {
      state,
      columnsHistory,
    },
    {
      getDiagnosisSelectProps,
      handleDiagnosisChange,
      toggleModalDetail,
      onChangeDiagnoses,
      loadingHistoryByPatient,
    },
  ];
}
